import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Share2, Trash2, ArrowLeft, AlertTriangle, Calendar, Plus, X } from 'lucide-react';
import PageLayout from '@/components/PageLayout';
import { useCar } from '@/contexts/CarContext';
import { useSettings } from '@/contexts/SettingsContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import SymptomRecurrenceForm from '@/components/SymptomRecurrenceForm';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';
import { useI18n } from '@/i18n/LanguageContext';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SymptomSummaryPage = () => {
  const { carId, issueId } = useParams<{ carId: string; issueId: string }>();
  const [searchParams] = useSearchParams();
  const { cars, updateIssue, deleteIssue, deleteRecurrence } = useCar();
  const { t, settings } = useSettings();
  const { t: tI18n } = useI18n();
  const navigate = useNavigate();
  const { toast } = useToast();
  const tab = searchParams.get('tab') || 'issues';
  const carIdFromParams = searchParams.get('carId') || carId;
  
  const car = cars.find((c) => c.id === carId);
  const issue = car?.issues?.find((i) => i.id === issueId);
  
  const [symptoms, setSymptoms] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [notesInitialized, setNotesInitialized] = useState(false);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recurrenceDialogOpen, setRecurrenceDialogOpen] = useState(false);
  const [deleteRecurrenceId, setDeleteRecurrenceId] = useState<string | null>(null);
  const [mediaImages, setMediaImages] = useState<string[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [symptomsLoading, setSymptomsLoading] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(-1);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const panStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const [associatedServiceEntryId, setAssociatedServiceEntryId] = useState<string | null>(null);
  
  useEffect(() => {
    if (issue && !symptomsLoading && !imagesLoading) {
      // We'll load symptoms asynchronously
      loadSymptoms(issue);
      
      setNotes(issue.notes || '');
      setNotesInitialized(false); // Reset flag when issue changes
      // Enable autosave after a brief delay to prevent saving on initial load
      setTimeout(() => setNotesInitialized(true), 100);
      
      // Load media images linked to this symptom from media table
      loadMediaImages();
    }
  }, [issue?.id]); // Only depend on issue ID, not the entire issue object

  // Load associated service entry if issue is resolved
  useEffect(() => {
    if (!issueId || !carId || !issue || issue.status !== 'resolved') {
      setAssociatedServiceEntryId(null);
      return;
    }

    const loadAssociatedServiceEntry = async () => {
      try {
        // Query service entries that resolved this issue
        const { data, error } = await supabase
          .from('service_entries')
          .select('id, issues_resolved_ids, service_date')
          .eq('car_id', carId)
          .not('issues_resolved_ids', 'is', null)
          .order('service_date', { ascending: false });

        if (error) {
          console.warn('Failed to load service entries for issue mapping:', error);
          setAssociatedServiceEntryId(null);
          return;
        }

        // Find the service entry that resolved this issue
        if (data) {
          for (const entry of data) {
            const resolvedIds = entry.issues_resolved_ids || [];
            if (Array.isArray(resolvedIds) && resolvedIds.includes(issueId)) {
              setAssociatedServiceEntryId(entry.id);
              return;
            }
          }
        }

        setAssociatedServiceEntryId(null);
      } catch (error) {
        console.error('Error loading associated service entry:', error);
        setAssociatedServiceEntryId(null);
      }
    };

    loadAssociatedServiceEntry();
  }, [issueId, carId, issue?.status]);

  // Autosave notes with debounce
  useEffect(() => {
    // Don't save on initial load or if notes haven't been initialized
    if (!notesInitialized || !issueId || !carId) {
      return;
    }

    // Debounce: wait 1.5 seconds after user stops typing
    const timeoutId = setTimeout(async () => {
      setIsSavingNotes(true);
      try {
        const { error } = await supabase
          .from('symptoms')
          .update({ notes })
          .eq('id', issueId);
        
        if (error) throw error;
        
        // Update local state
        updateIssue(carId, issueId, { notes });
        setIsSavingNotes(false);
      } catch (error) {
        console.error('Error autosaving notes:', error);
        setIsSavingNotes(false);
        toast({
          title: tI18n('symptomSummary.toasts.notesErrorTitle') || 'Error',
          description: tI18n('symptomSummary.toasts.notesErrorDesc') || 'Failed to save notes',
          variant: "destructive"
        });
      }
    }, 1500); // 1.5 second debounce

    return () => clearTimeout(timeoutId);
  }, [notes, notesInitialized, issueId, carId, updateIssue, toast]);

  // Ensure default status is 'draft' if missing - only run once
  useEffect(() => {
    if (car && issue && !issue.status && carId && issueId) {
      updateIssue(carId, issueId, { status: 'draft' });
    }
  }, [car?.id, issue?.id, carId, issueId]); // Only depend on IDs, not objects or functions

  // Status styles matching RecordsPage
  const statusStyles = useMemo(() => ({
    draft: { bg: '#ffffff', text: '#6b7280', border: '#CBCBCB', needsBorder: true },
    open: { bg: '#eff2fc', text: '#7c9ffa', border: '#7c9ffa' },
    recurred: { bg: '#defbfc', text: '#007a85', border: '#00e0e8' },
    resolved: { bg: '#e9fce9', text: '#2d7a42', border: '#2d7a42' },
  } as Record<string, { bg: string; text: string; border: string; needsBorder?: boolean }>), []);

  // Handle ESC key, arrow keys, and zoom for full screen image
  useEffect(() => {
    if (!fullScreenImage) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFullScreenImage(null);
        setCurrentImageIndex(-1);
        setImageZoom(1);
        setImageOffset({ x: 0, y: 0 });
      } else if (e.key === 'ArrowLeft' && currentImageIndex > 0) {
        setCurrentImageIndex(currentImageIndex - 1);
        setFullScreenImage(mediaImages[currentImageIndex - 1]);
        setImageZoom(1);
        setImageOffset({ x: 0, y: 0 });
      } else if (e.key === 'ArrowRight' && currentImageIndex < mediaImages.length - 1) {
        setCurrentImageIndex(currentImageIndex + 1);
        setFullScreenImage(mediaImages[currentImageIndex + 1]);
        setImageZoom(1);
        setImageOffset({ x: 0, y: 0 });
      }
    };

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setImageZoom(prev => Math.max(1, Math.min(5, prev + delta)));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('wheel', handleWheel, { passive: false });
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('wheel', handleWheel);
      document.body.style.overflow = 'unset';
    };
  }, [fullScreenImage, currentImageIndex, mediaImages]);
  
  const loadMediaImages = async () => {
    if (imagesLoading) return; // Prevent multiple simultaneous calls
    
    try {
      if (!issueId) return;
      setImagesLoading(true);

      // Fetch media rows for this symptom
      const { data, error } = await supabase
        .from('media')
        .select('url, type, media_kind')
        .eq('symptom_id', issueId)
        .eq('type', 'image');

      if (error) {
        console.error('Error fetching media for symptom:', error);
        setMediaImages([]);
        setImagesLoading(false);
        return;
      }

      const paths = (data || [])
        .filter((m) => !!m?.url)
        .map((m) => m.url as string);

      if (paths.length === 0) {
        setMediaImages([]);
        setImagesLoading(false);
        return;
      }

      // Sign each GCS path into a temporary view URL
      const signed = await Promise.all(
        paths.map(async (p) => {
          try {
            const info = await getSignedViewUrl(p);
            return info.url;
          } catch (e) {
            console.warn('Failed to sign view URL for', p, e);
            return null;
          }
        })
      );

      setMediaImages(signed.filter((u): u is string => !!u));
      setImagesLoading(false);
    } catch (e) {
      console.error('Unexpected error loading media images:', e);
      setMediaImages([]);
      setImagesLoading(false);
    }
  };
  
  const loadSymptoms = async (currentIssue) => {
    if (symptomsLoading) return; // Prevent multiple simultaneous calls
    
    setSymptomsLoading(true);
    
    try {
    // Start with basic extraction from description
    let extractedSymptoms = extractSymptomsFromText(currentIssue.description);
    
    // Extract symptoms from notes if available
    if (currentIssue.notes) {
      const noteSymptoms = extractSymptomsFromText(currentIssue.notes);
      extractedSymptoms = [...extractedSymptoms, ...noteSymptoms];
    }
    
    // Process symptoms from AI Q&A with improved synthesis
    if (currentIssue.aiQuestions && currentIssue.aiAnswers) {
      const relevantQAPairs = currentIssue.aiQuestions
        .map((q, i) => ({ 
          question: q, 
          answer: currentIssue.aiAnswers?.[i] || '' 
        }))
        .filter(qa => 
          qa.answer && 
          qa.answer.trim() !== '' && 
          !qa.answer.toLowerCase().includes('no') && 
          !qa.answer.toLowerCase().includes('not') &&
          !qa.answer.toLowerCase().includes('nav')
        );
      
      if (relevantQAPairs.length > 0) {
        // Process each Q&A pair with OpenAI
        const synthesizedSymptoms = await Promise.all(
          relevantQAPairs.map(async (qa) => {
            try {
              // Detect language from the answer
              const language = detectLanguage(qa.answer);
              
              // Call the OpenAI function to synthesize a statement
              const { data, error } = await supabase.functions.invoke('synthesize-symptoms', {
                body: { 
                  questionAnswer: {
                    question: qa.question,
                    answer: qa.answer
                  },
                  language
                }
              });
              
              if (error) {
                console.error('Error synthesizing Q&A:', error);
                return fallbackCombineQuestionAndAnswer(qa.question, qa.answer);
              }
              
              return data.result || fallbackCombineQuestionAndAnswer(qa.question, qa.answer);
            } catch (err) {
              console.error('Error processing Q&A:', err);
              return fallbackCombineQuestionAndAnswer(qa.question, qa.answer);
            }
          })
        );
        
        extractedSymptoms = [...extractedSymptoms, ...synthesizedSymptoms];
      }
    }
    
    // Remove duplicates and filter out empty strings
    const uniqueSymptoms = [...new Set(extractedSymptoms)]
      .filter(s => s.trim().length > 0)
      .slice(0, 7); // Limit to a reasonable number
    
    setSymptoms(uniqueSymptoms.length ? uniqueSymptoms : ['No specific symptoms identified']);
    } catch (error) {
      console.error('Error loading symptoms:', error);
      setSymptoms(['Error loading symptoms']);
    } finally {
      setSymptomsLoading(false);
    }
  };
  
  const detectLanguage = (text: string): string => {
    // Latvian-specific characters
    const latvianChars = ['ā', 'č', 'ē', 'ģ', 'ī', 'ķ', 'ļ', 'ņ', 'š', 'ū', 'ž'];
    
    // Convert to lowercase for better matching
    const lowerText = text.toLowerCase();
    
    // Check if any Latvian characters are present
    const hasLatvianChars = latvianChars.some(char => lowerText.includes(char));
    
    return hasLatvianChars ? 'Latvian' : 'English';
  };
  
  const extractSymptomsFromText = (text: string): string[] => {
    if (!text || text.trim().length === 0) return [];
    
    // Split the text into sentences
    const sentences = text
      .split(/[.!?]/)
      .map(s => s.trim())
      .filter(s => s.length > 5);
    
    // Basic filtering for symptom-like statements
    return sentences.filter(sentence => {
      // Exclude section headers and metadata
      if (sentence.toLowerCase().includes('additional symptoms from') ||
          sentence.toLowerCase().includes('recurrence on')) {
        return false;
      }
      
      return sentence.length > 10;
    });
  };
  
  const fallbackCombineQuestionAndAnswer = (question: string, answer: string): string => {
    // Clean up the question by removing question marks and common prefixes
    const cleanQuestion = question
      .replace(/\?+$/, '')
      .replace(/^(do you|does it|is it|are there|have you|can you|when do you)/i, '')
      .replace(/^(notice|experience|hear|feel|observe)/i, '')
      .trim();
    
    // Clean up the answer by removing common prefixes
    const cleanAnswer = answer
      .replace(/^(yes|yeah|yep|correct|definitely|absolutely)/i, '')
      .trim();
    
    // If the answer is just an affirmation without details, return a formatted statement
    if (cleanAnswer.length < 3) {
      // Convert question format to statement format
      return `The issue includes ${cleanQuestion.toLowerCase()}`;
    }
    
    // Special case for "when" questions 
    if (question.toLowerCase().includes("when")) {
      return `The issue occurs ${cleanAnswer.toLowerCase()}`;
    }
    
    // Special case for "how often" questions
    if (question.toLowerCase().includes("how often") || question.toLowerCase().includes("frequency")) {
      return `The issue happens ${cleanAnswer.toLowerCase()}`;
    }
    
    // Check if the answer already contains the essence of the question
    const questionKeywords = cleanQuestion.toLowerCase().split(' ').filter(word => word.length > 4);
    const answerContainsQuestion = questionKeywords.some(keyword => 
      cleanAnswer.toLowerCase().includes(keyword)
    );
    
    if (answerContainsQuestion) {
      // The answer already includes context from the question
      return cleanAnswer.charAt(0).toUpperCase() + cleanAnswer.slice(1);
    } else {
      // Combine them in a way that makes sense
      return `${cleanQuestion.charAt(0).toUpperCase() + cleanQuestion.slice(1)} - ${cleanAnswer}`;
    }
  };
  
  if (!car || !issue) {
    return (
      <PageLayout 
        title={tI18n('symptomSummary.notFound.title') || 'Not found'}
        showBackButton 
        onBack={() => {
          if (carIdFromParams) {
            navigate(`/records/${carIdFromParams}?tab=${tab}`);
          } else {
            navigate(-1);
          }
        }}
      >
        <div className="text-center py-12">
          <p>{tI18n('symptomSummary.notFound.message') || "The issue you're looking for doesn't exist or has been deleted."}</p>
          <Button 
            onClick={() => navigate('/reports')}
            className="mt-4"
          >
            {tI18n('symptomSummary.notFound.backToReports') || 'Back to reports'}
          </Button>
        </div>
      </PageLayout>
    );
  }
  
  
  const handleShare = () => {
    toast({
      title: tI18n('symptomSummary.toasts.shareTitle') || "Sharing symptom summary",
      description: tI18n('symptomSummary.toasts.shareDesc') || "A shareable link has been generated and copied to your clipboard",
    });
  };
  
  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };
  
  const confirmDelete = async () => {
    try {
      // Delete from database
      const { error } = await supabase
        .from('symptoms')
        .delete()
        .eq('id', issueId);
      
      if (error) throw error;
      
      // Delete from local state
    deleteIssue(carId, issueId);
      
    toast({
      title: tI18n('symptomSummary.toasts.deleteSuccessTitle') || "Symptom log deleted",
      description: tI18n('symptomSummary.toasts.deleteSuccessDesc') || "The symptom log has been removed",
    });
      
    navigate('/reports');
    } catch (error) {
      console.error('Error deleting symptom:', error);
      toast({
        title: tI18n('symptomSummary.toasts.deleteErrorTitle') || "Error",
        description: tI18n('symptomSummary.toasts.deleteErrorDesc') || "Failed to delete symptom",
        variant: "destructive"
      });
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };
  
  const handleAddRecurrence = () => {
    setRecurrenceDialogOpen(true);
  };
  
  const handleDeleteRecurrence = (recurrenceId: string) => {
    setDeleteRecurrenceId(recurrenceId);
  };
  
  const confirmDeleteRecurrence = () => {
    if (deleteRecurrenceId && carId && issueId) {
      deleteRecurrence(carId, issueId, deleteRecurrenceId);
      setDeleteRecurrenceId(null);
      
      toast({
      title: tI18n('symptomSummary.toasts.recurrenceRemovedTitle') || "Recurrence removed",
      description: tI18n('symptomSummary.toasts.recurrenceRemovedDesc') || "The recurrence has been deleted"
      });
    }
  };
  
  const cancelDeleteRecurrence = () => {
    setDeleteRecurrenceId(null);
  };
  
  const handleBack = () => {
    if (carIdFromParams) {
      navigate(`/records/${carIdFromParams}?tab=${tab}`);
    } else {
      navigate(-1);
    }
  };

  return (
    <PageLayout 
      title={tI18n('symptomSummary.title') || ''}
      showBackButton
      noBottomPadding
      onBack={handleBack}
      className="bg-[#f5f5f5]"
    >
      <div className="w-full font-lato text-black p-4">
        {/* HEADER */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-4">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-lg font-bold">{issue.title}</h1>
              <p className="text-xs text-gray-500 mt-1">
                {format(new Date(issue.date), "dd MMM yyyy, HH:mm")}
                {' • '}
                {car.brand} {car.model}
              </p>
            </div>
            {(() => {
              const status = (issue.status || 'draft') as string;
              const ss = statusStyles[status] || statusStyles.draft;
              return (
                <span
                  className="text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap"
                  style={{
                    background: ss.bg,
                    color: ss.text,
                    fontFamily: 'Lato',
                    border: ss.needsBorder ? '1px solid #D1D5DB' : 'none',
                  }}
                >
                  {status === 'draft'
                    ? (tI18n('symptomSummary.header.draft') || 'Draft')
                    : status === 'open'
                      ? (tI18n('symptomSummary.header.open') || 'Open')
                      : status === 'recurred'
                        ? (tI18n('symptomSummary.header.recurred') || 'Recurred')
                        : status === 'resolved'
                          ? (tI18n('symptomSummary.header.resolved') || 'Resolved')
                          : String(status)}
                </span>
              );
            })()}
          </div>
        </div>

        {/* AI SUMMARY */}
        {issue.aiSummary && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">{tI18n('symptomSummary.sections.summary') || 'Summary'}</h2>
            <div className="bg-white rounded-xl shadow-md p-4">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{issue.aiSummary}</p>
            </div>
          </section>
        )}

        {/* STATUS */}
        <section className="mt-4 mb-4">
          <h2 className="text-lg font-semibold mb-2">{tI18n('symptomSummary.sections.status') || 'Status'}</h2>
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="flex justify-between items-center">
            <span className="text-sm text-gray-700">{tI18n('symptomSummary.header.currentStatus') || 'Current status:'}</span>
            <Select
              value={(issue.status as 'draft' | 'open' | 'recurred' | 'resolved') ?? 'draft'}
              onValueChange={async (value) => {
                try {
                  await updateIssue(carId, issueId, { 
                    status: value as 'draft' | 'open' | 'recurred' | 'resolved'
                  });
                } catch (error) {
                  console.error('Failed to update issue status:', error);
                }
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={tI18n('symptomSummary.header.status') || 'Status'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">{tI18n('symptomSummary.header.draft') || 'Draft'}</SelectItem>
                <SelectItem value="open">{tI18n('symptomSummary.header.open') || 'Open'}</SelectItem>
                <SelectItem value="recurred">{tI18n('symptomSummary.header.recurred') || 'Recurred'}</SelectItem>
                <SelectItem value="resolved">{tI18n('symptomSummary.header.resolved') || 'Resolved'}</SelectItem>
              </SelectContent>
            </Select>
            </div>
          </div>
        </section>

        {/* ASSOCIATED REPAIR */}
        {issue.status === 'resolved' && associatedServiceEntryId && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">{tI18n('symptomSummary.sections.associatedRepair') || 'Associated repair'}</h2>
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="text-sm text-gray-700">
                <button
                  type="button"
                  onClick={() => navigate(`/services/view/${associatedServiceEntryId}?tab=repairs&carId=${carId || ''}`)}
                  className="text-[#7c9ffa] hover:underline font-medium"
                >
                  {tI18n('symptomSummary.sections.associatedRepairView') || 'View record'}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* VIDEOS */}
        {(issue.videos?.length || 0) > 0 && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">{tI18n('symptomSummary.sections.uploadedVideos') || 'Uploaded videos'}</h2>
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex gap-2 overflow-x-auto pb-2">
              {issue.videos?.map((url, index) => (
                <video 
                  key={index}
                  src={url} 
                  className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                  controls
                />
              ))}
              </div>
            </div>
          </section>
        )}

        {/* ANALYSIS */}
        {(issue.possibleCauses?.length > 0 || issue.aiRecommendations?.length > 0 || issue.explanation) && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">{tI18n('symptomSummary.sections.analysis') || 'Analysis'}</h2>
            <div className="bg-white rounded-xl shadow-md p-4">
              {/* Possible Causes */}
            {issue.possibleCauses && issue.possibleCauses.length > 0 && (
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">{tI18n('symptomSummary.sections.possibleCauses') || 'Possible causes'}</h3>
                <ul className="list-disc space-y-1 text-sm text-gray-700 pl-4">
                  {issue.possibleCauses.map((cause, index) => (
                    <li key={index}>{cause}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Recommendations */}
            {issue.aiRecommendations && issue.aiRecommendations.length > 0 && (
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">{tI18n('symptomSummary.sections.recommendations') || 'Recommendations'}</h3>
                <ul className="list-disc space-y-1 text-sm text-gray-700 pl-4">
                  {issue.aiRecommendations.map((recommendation, index) => (
                    <li key={index}>{recommendation}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Explanation */}
            {issue.explanation && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">{tI18n('symptomSummary.sections.detailedExplanation') || 'Detailed explanation'}</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{issue.explanation}</p>
              </div>
            )}
            </div>
          </section>
        )}

        {/* DIY CHECK */}
        {issue.diyCheck && Array.isArray(issue.diyCheck) && issue.diyCheck.length > 0 && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">{tI18n('symptomSummary.sections.diyCheck') || 'DIY check items'}</h2>
            <div className="bg-white rounded-xl shadow-md p-4">
              <ul className="list-disc space-y-1 text-sm text-gray-700 pl-4">
              {issue.diyCheck.map((check, index) => (
                <li key={index}>{check}</li>
              ))}
              </ul>
            </div>
          </section>
        )}

        {/* URGENCY LEVEL */}
        {issue.urgencyLevel && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">{tI18n('symptomSummary.sections.attentionLevel') || 'Attention level'}</h2>
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex items-center gap-3">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((level) => (
                  <div key={level} className={`w-4 h-4 rounded-full ${level <= (issue.urgencyLevel as number) ? 'bg-red-500' : 'bg-gray-300'}`} />
                ))}
              </div>
              <span className="text-sm font-medium text-gray-700">
                {(issue.urgencyLevel === 1 && (tI18n('symptomSummary.urgencyLabels.low') || 'Low')) ||
                  (issue.urgencyLevel === 3 && (tI18n('symptomSummary.urgencyLabels.medium') || 'Medium')) ||
                  (issue.urgencyLevel === 5 && (tI18n('symptomSummary.urgencyLabels.high') || 'High')) ||
                  (tI18n('symptomSummary.urgencyLabels.medium') || 'Medium')}
              </span>
              </div>
            </div>
          </section>
        )}

        {/* UPLOADED IMAGES */}
        {mediaImages.length > 0 && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">
              {(tI18n('symptomSummary.sections.uploadedImagesNoCount') || 'Uploaded images') + ` (${mediaImages.length})`}
            </h2>
            <div className="bg-white rounded-xl shadow-md p-4">
              {imagesLoading && (
                <div className="text-sm text-gray-500 mb-2">
                  {tI18n('symptomSummary.sections.loadingImages') || 'Loading images...'}
                </div>
              )}
              <div className="flex gap-2 overflow-x-auto pb-2">
              {mediaImages.map((url, index) => (
                <div 
                  key={index} 
                  className="flex flex-col items-center cursor-pointer"
                  onClick={() => {
                    const index = mediaImages.indexOf(url);
                    setCurrentImageIndex(index);
                    setFullScreenImage(url);
                    setImageZoom(1);
                    setImageOffset({ x: 0, y: 0 });
                  }}
                >
                  <img 
                    src={url} 
                    alt={`Issue ${index + 1}`} 
                    className="w-24 h-24 object-cover rounded-lg border border-gray-200" 
                    onError={() => console.error('Image failed to load:', url)} 
                  />
                </div>
              ))}
              </div>
            </div>
          </section>
        )}

        {/* ADDITIONAL NOTES */}
        <section className="mt-4 mb-4">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold">{tI18n('symptomSummary.sections.additionalNotes') || 'Additional notes'}</h2>
            {isSavingNotes && (
              <span className="text-xs text-gray-500">{tI18n('symptomSummary.notes.saving') || 'Saving...'}</span>
            )}
          </div>
          <div className="bg-white rounded-xl shadow-md p-4">
            <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
              className="min-h-[80px] resize-y text-sm w-full"
              placeholder={tI18n('symptomSummary.notes.placeholder') || 'Add any additional notes here...'}
            />
          </div>
        </section>

        {/* ACTION BUTTONS */}
        <div className="mt-4 mb-4 pb-4 flex gap-2">
          <Button
            variant="destructive"
            className="flex-1 py-3 rounded-xl text-sm font-medium"
            onClick={handleDelete}
            type="button"
          >
            <Trash2 size={16} className="mr-1" />
            {tI18n('symptomSummary.actions.delete') || 'Delete'}
          </Button>
          {settings.mechanicSharingEnabled && (
            <Button
              className="flex-1 py-3 rounded-xl bg-[#e7fe52] text-gray-900 text-sm font-medium hover:bg-[#e7fe52]/90"
              onClick={handleShare}
              type="button"
            >
              <Share2 size={16} className="mr-1" />
              {tI18n('symptomSummary.actions.share') || 'Share'}
            </Button>
          )}
        </div>
      </div>
      
      {/* Alert Dialogs */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tI18n('symptomSummary.dialogs.deleteTitle') || 'Delete symptom log'}</AlertDialogTitle>
            <AlertDialogDescription>
              {tI18n('symptomSummary.dialogs.deleteDescription') || 'Are you sure you want to delete this symptom log? This action cannot be undone and will remove all data from the database and storage.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDelete}>{tI18n('symptomSummary.dialogs.cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 text-white hover:bg-red-600">
              {tI18n('symptomSummary.dialogs.confirmDelete') || 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <AlertDialog open={deleteRecurrenceId !== null} onOpenChange={cancelDeleteRecurrence}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tI18n('symptomSummary.dialogs.deleteRecurrenceTitle') || 'Delete recurrence'}</AlertDialogTitle>
            <AlertDialogDescription>
              {tI18n('symptomSummary.dialogs.deleteRecurrenceDescription') || 'Are you sure you want to delete this recurrence? This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDeleteRecurrence}>{tI18n('symptomSummary.dialogs.cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteRecurrence} className="bg-red-500 text-white hover:bg-red-600">
              {tI18n('symptomSummary.dialogs.confirmDelete') || 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Full Screen Image Modal */}
      {fullScreenImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => {
            if (imageZoom === 1) {
              setFullScreenImage(null);
              setCurrentImageIndex(-1);
              setImageZoom(1);
              setImageOffset({ x: 0, y: 0 });
            }
          }}
        >
          <button
            onClick={() => {
              setFullScreenImage(null);
              setCurrentImageIndex(-1);
              setImageZoom(1);
              setImageOffset({ x: 0, y: 0 });
            }}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            aria-label="Close"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Previous Button */}
          {mediaImages.length > 1 && currentImageIndex > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newIndex = currentImageIndex - 1;
                setCurrentImageIndex(newIndex);
                setFullScreenImage(mediaImages[newIndex]);
                setImageZoom(1);
                setImageOffset({ x: 0, y: 0 });
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2"
              aria-label="Previous image"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}

          {/* Next Button */}
          {mediaImages.length > 1 && currentImageIndex < mediaImages.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newIndex = currentImageIndex + 1;
                setCurrentImageIndex(newIndex);
                setFullScreenImage(mediaImages[newIndex]);
                setImageZoom(1);
                setImageOffset({ x: 0, y: 0 });
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2"
              aria-label="Next image"
            >
              <ArrowLeft className="w-6 h-6 rotate-180" />
            </button>
          )}

          {/* Image Counter */}
          {mediaImages.length > 1 && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full z-10">
              {currentImageIndex + 1} / {mediaImages.length}
            </div>
          )}

          {/* Image Container with Zoom and Pan */}
          <div
            className="relative w-full h-full flex items-center justify-center touch-none"
            onWheel={(e) => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const delta = e.deltaY > 0 ? -0.1 : 0.1;
                setImageZoom(prev => Math.max(1, Math.min(5, prev + delta)));
              }
            }}
            onMouseDown={(e) => {
              if (imageZoom > 1 && e.button === 0) {
                panStartRef.current = { x: e.clientX - imageOffset.x, y: e.clientY - imageOffset.y };
              }
            }}
            onMouseMove={(e) => {
              if (panStartRef.current && imageZoom > 1) {
                setImageOffset({
                  x: e.clientX - panStartRef.current.x,
                  y: e.clientY - panStartRef.current.y,
                });
              }
            }}
            onMouseUp={() => {
              panStartRef.current = null;
            }}
            onMouseLeave={() => {
              panStartRef.current = null;
            }}
            onTouchStart={(e) => {
              if (e.touches.length === 1 && imageZoom === 1) {
                // Single touch for swipe
                const touch = e.touches[0];
                panStartRef.current = { x: touch.clientX, y: touch.clientY };
              }
            }}
            onTouchMove={(e) => {
              if (e.touches.length === 1 && imageZoom === 1 && panStartRef.current) {
                const touch = e.touches[0];
                const deltaX = touch.clientX - panStartRef.current.x;
                // Swipe threshold: 50px
                if (Math.abs(deltaX) > 50) {
                  if (deltaX > 0 && currentImageIndex > 0) {
                    // Swipe right - previous image
                    const newIndex = currentImageIndex - 1;
                    setCurrentImageIndex(newIndex);
                    setFullScreenImage(mediaImages[newIndex]);
                    panStartRef.current = null;
                  } else if (deltaX < 0 && currentImageIndex < mediaImages.length - 1) {
                    // Swipe left - next image
                    const newIndex = currentImageIndex + 1;
                    setCurrentImageIndex(newIndex);
                    setFullScreenImage(mediaImages[newIndex]);
                    panStartRef.current = null;
                  }
                }
              }
            }}
            onTouchEnd={() => {
              panStartRef.current = null;
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={fullScreenImage}
              alt="Full screen"
              className="max-w-full max-h-full object-contain select-none"
              style={{
                transform: imageZoom > 1 
                  ? `translate(${imageOffset.x}px, ${imageOffset.y}px) scale(${imageZoom})`
                  : 'scale(1)',
                transformOrigin: 'center center',
                cursor: imageZoom > 1 ? 'grab' : 'default',
                transition: imageZoom === 1 ? 'transform 0.2s' : 'none',
              }}
              draggable={false}
            />
          </div>

          {/* Zoom Indicator */}
          {imageZoom > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-xs bg-black/50 px-3 py-1 rounded-full z-10">
              {Math.round(imageZoom * 100)}%
            </div>
          )}
        </div>
      )}
      

      
      {carId && issueId && issue && (
        <SymptomRecurrenceForm
          open={recurrenceDialogOpen}
          onOpenChange={setRecurrenceDialogOpen}
          carId={carId}
          issueId={issueId}
          issueName={issue.title}
        />
      )}
    </PageLayout>
  );
};

export default SymptomSummaryPage;
